// src/trade-drives/trade-drives.js — Trade Drives KPI sub-tab (v87.0)
//
// Extracted from index.html (lines 9606-10565) in v86.11. Previously
// inline as `var TradeDrives = (function(){ ... })()`. Now an ES module
// bundled by esbuild into dist/trade-drives.js with global name
// `TradeDrives` (see build.js MODULES entry).
//
// Public surface (consumed by inline onclick handlers in index.html):
//   TradeDrives.onTabOpen()
//   TradeDrives.setPeriod(period)
//   TradeDrives.setChart(chart)
//   TradeDrives.onStoreToggle(input)
//   TradeDrives.selectAllStores(all)
//
// Free variables — looked up at runtime via the global lexical scope.
// Per the staff.js precedent (src/top50/staff.js:19-25), top-level
// `let`/`const` declarations in index.html do NOT attach to window, so
// these MUST stay as bare identifiers (do NOT rewrite to window.X):
//   currentUser     — `let currentUser` at index.html:2329
//   ALL_STORES      — `let ALL_STORES` at index.html:2213
//   currentStore    — `let currentStore` (rota module state in index.html)
//   DB              — `const DB` from db.js
//   escapeHtml      — function declaration at index.html:4631 (attaches
//                     to window, bare identifier works either way)
//   Top50.shared.*  — global from dist/top50.js bundle (window-attached
//                     by esbuild's globalName)
//
// Original IIFE body opened by:
//   1. A comment header block (lines 9606-9638 of index.html v86.10)
//   2. Module state vars
// — both preserved below.
//
// ════════════════════════════════════════════════════════════
// TRADE DRIVES — extracted from v84.0 (originally), maintained
// through v84.1, v84.2, v85.0, v86.1, v86.2, v86.3, v86.4
// ════════════════════════════════════════════════════════════
//
// A comparison playground in the KPI screen. Three pieces:
//
//   1. Period selector (WTD, Last Week, MTD, Last Month, YTD).
//   2. Multi-store overlay constrained by permission — store managers see
//      their store only, area managers see their area, admins see all.
//   3. Three chart modes the user can flip between:
//        bars     — side-by-side Sales / Budget / LY per store
//        lines    — daily sales trend, one line per store
//        heatmap  — ranked % achievement table with cell shading
//
// Below the chart sits the trade-drive overlay: items in the current
// Top 50 report where the primary store has SOH > 0 but qty = 0 in the
// week. That's the "what should I push?" insight — it lets store teams
// improve their numbers WITHOUT calling the buying team. Just rearrange
// the display, talk it up with staff, demo it on the till.
//
// Data sources (all existing endpoints — no new worker code in v84.0):
//   - /api/kpi_comparison?from=&to=  → per-store sales+target+LY for a range
//   - /api/top50/reports / /api/top50/entries → latest Top 50 with store-level
//     qty and SOH per item
//   - currentUser / ALL_STORES already in scope — drives permission gating
//
// Permissions:
//   - Store manager  → tdStoreList shows only currentUser.store (locked on)
//   - Area manager   → tdStoreList shows every store in their area
//   - Admin          → tdStoreList shows every active store
//
// SVG charts are built by hand. The PWA caches everything via service worker
// and we'd rather not introduce a chart library that breaks offline boot.

// Module state.
var period = 'wtd';           // 'wtd' | 'lastweek' | 'mtd' | 'lastmonth' | 'ytd' | 'custom'
var chart  = 'bars';          // 'bars' | 'lines' | 'heatmap' | 'addons'
var primaryStore = null;      // The user's home store (or first available)
var selectedStores = [];      // Stores currently overlaid
var availableStores = [];     // Stores the user has permission to see
var lastData = null;          // Cached /api/kpi_comparison result
var lastDaily = {};           // v84.1 — per-store daily series for Trend chart
var lastTop50 = null;         // Cached Top 50 entries (volume metric)
var lastAddonRates = null;    // v84.2 — addon prices row from /api/addon_rates (cached, doesn't change often)
var customFrom = '';          // custom range start (YYYY-MM-DD)
var customTo   = '';          // custom range end (YYYY-MM-DD)
var areaNameById = null;      // cached area_id → name (from DB.areas.list)

function fmtMoney(n) { return 'AED ' + Math.round(Number(n) || 0).toLocaleString(); }

// Period maths in Dubai TZ (UTC+4) — matches localDateStr() elsewhere.
function dubaiToday() {
  var d = new Date(Date.now() + 4 * 3600 * 1000);
  return d.toISOString().slice(0, 10);
}
function ymd(d){ return d.toISOString().slice(0, 10); }
function rangeFor(p) {
  var todayStr = dubaiToday();
  var today = new Date(todayStr + 'T00:00:00Z');
  // v86.13: upper bound for "X to date" periods is YESTERDAY (last
  // completed day) — sales lag by one day vs. targets. The `todayStr`
  // above is kept for COMPUTING period starts (Monday-of-this-week,
  // first-of-this-month) which depend on today's date. See dates.js
  // effectiveToday() for full rationale + edge-case handling.
  var effToday = effectiveToday();
  if (p === 'custom') {
    var cf = customFrom || effToday;
    var ct = customTo || effToday;
    return (cf <= ct) ? { from: cf, to: ct } : { from: ct, to: cf };
  }
  if (p === 'wtd') {
    // Monday → yesterday. JS getUTCDay: Sun=0, Mon=1, ... Sat=6.
    // Days to subtract = ((dow + 6) % 7) so Mon → 0.
    var dow = today.getUTCDay();
    var back = (dow + 6) % 7;
    var monday = new Date(today); monday.setUTCDate(monday.getUTCDate() - back);
    return { from: ymd(monday), to: effToday };
  }
  if (p === 'lastweek') {
    // Past period, not affected by today-vs-yesterday — both bounds in past.
    var dow2 = today.getUTCDay();
    var back2 = (dow2 + 6) % 7;
    var thisMonday = new Date(today); thisMonday.setUTCDate(thisMonday.getUTCDate() - back2);
    var lastMonday = new Date(thisMonday); lastMonday.setUTCDate(lastMonday.getUTCDate() - 7);
    var lastSunday = new Date(thisMonday); lastSunday.setUTCDate(lastSunday.getUTCDate() - 1);
    return { from: ymd(lastMonday), to: ymd(lastSunday) };
  }
  if (p === 'mtd') {
    var first = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 1));
    return { from: ymd(first), to: effToday };
  }
  if (p === 'lastmonth') {
    // Past period, not affected — both bounds in past.
    var firstThis = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 1));
    var lastEnd = new Date(firstThis); lastEnd.setUTCDate(lastEnd.getUTCDate() - 1);
    var lastStart = new Date(Date.UTC(lastEnd.getUTCFullYear(), lastEnd.getUTCMonth(), 1));
    return { from: ymd(lastStart), to: ymd(lastEnd) };
  }
  // ytd
  var jan1 = new Date(Date.UTC(today.getUTCFullYear(), 0, 1));
  return { from: ymd(jan1), to: effToday };
}
function periodLabel(p) {
  var r = rangeFor(p);
  var labels = { wtd: 'Week to date', lastweek: 'Last week', mtd: 'Month to date', lastmonth: 'Last month', ytd: 'Year to date', custom: 'Custom range' };
  return (labels[p] || p) + ' · ' + r.from + ' → ' + r.to;
}

// ── Permission-based store list ─────────────────────────────
// v84.1 — fixed: ALL_STORES is an array of OBJECTS
// ({ name, area_id, country, active }), NOT strings as the v84.0 code
// assumed. Field is area_id (with underscore), not area. _storeMetaCache
// doesn't exist in this scope — ALL_STORES is the source of metadata.
//
// currentUser shape (set at login):
//   { name, role: 'admin' | 'area_manager' | 'staff', area: number|null,
//     store: string|null }
//
// Permission ladder:
//   admin       → every active store
//   area_manager → stores whose area_id matches currentUser.area
//   staff/other → just currentUser.store (locked on)
function permittedStores() {
  var role = (currentUser && currentUser.role) || '';
  var meta = Array.isArray(ALL_STORES) ? ALL_STORES : [];
  // Strip the synthetic "Retail Relief Managers" virtual store —
  // it's not a real location and has no sales data.
  var realMeta = meta.filter(function(s){ return s && s.name && s.name !== 'Retail Relief Managers'; });

  if (role === 'admin' || role === 'super_admin') {
    return realMeta.map(function(s){ return s.name; });
  }
  if (role === 'area_manager') {
    var areaId = currentUser && currentUser.area;
    // Defensive: if the account has no area set, show every store so
    // we don't render an empty list. Logged as a followup.
    if (areaId == null) return realMeta.map(function(s){ return s.name; });
    var byArea = realMeta.filter(function(s){ return s.area_id === areaId }).map(function(s){ return s.name; });
    // If the filter returns nothing (mismatched area), fall back to the
    // user's home store rather than empty.
    return byArea.length ? byArea : (currentUser.store ? [currentUser.store] : []);
  }
  // Store manager / staff: just their home store.
  return currentUser && currentUser.store ? [currentUser.store] : [];
}

export function setPeriod(p) {
  period = p;
  ensureCustomControls();
  if (p === 'custom' && (!customFrom || !customTo)) initCustomDefaults();
  document.querySelectorAll('#tdPeriodButtons button').forEach(function(btn){
    var isActive = btn.getAttribute('data-period') === p;
    btn.style.background = isActive ? 'var(--green)' : 'var(--card)';
    btn.style.color      = isActive ? '#fff'         : 'var(--sub)';
    btn.style.borderColor= isActive ? 'var(--green)' : 'var(--border)';
  });
  var row = document.getElementById('tdCustomRange');
  if (row) row.style.display = (p === 'custom') ? 'flex' : 'none';
  var label = document.getElementById('tdPeriodLabel');
  if (label) label.textContent = periodLabel(p);
  load();
}

// ── Custom date range (v87: module-injected so no index.html change) ──────
function initCustomDefaults() {
  var to = effectiveToday();
  var d = new Date(to + 'T00:00:00Z'); d.setUTCDate(d.getUTCDate() - 6);
  customFrom = ymd(d); customTo = to;
  var f = document.getElementById('tdCustomFrom'), t = document.getElementById('tdCustomTo');
  if (f) f.value = customFrom;
  if (t) t.value = customTo;
}

function ensureCustomControls() {
  var btnRow = document.getElementById('tdPeriodButtons');
  if (!btnRow) return;
  if (!document.getElementById('tdPeriodCustomBtn')) {
    var b = document.createElement('button');
    b.id = 'tdPeriodCustomBtn';
    b.setAttribute('data-period', 'custom');
    b.textContent = 'Custom';
    b.style.cssText = 'padding:6px 12px;border:1px solid var(--border);background:var(--card);color:var(--sub);border-radius:6px;font-size:12px;font-weight:600;cursor:pointer;font-family:inherit';
    b.addEventListener('click', function(){ setPeriod('custom'); });
    btnRow.appendChild(b);
  }
  if (!document.getElementById('tdCustomRange')) {
    var row = document.createElement('div');
    row.id = 'tdCustomRange';
    row.style.cssText = 'display:none;gap:8px;align-items:center;margin-top:8px;flex-wrap:wrap';
    var inputStyle = 'padding:5px 8px;border:1px solid var(--border);border-radius:6px;font-size:12px;font-family:inherit';
    row.innerHTML =
      '<input type="date" id="tdCustomFrom" style="' + inputStyle + '">' +
      '<span style="font-size:12px;color:var(--sub)">to</span>' +
      '<input type="date" id="tdCustomTo" style="' + inputStyle + '">' +
      '<button id="tdCustomApply" style="padding:6px 12px;border:1px solid var(--green);background:var(--green);color:#fff;border-radius:6px;font-size:12px;font-weight:600;cursor:pointer;font-family:inherit">Apply</button>';
    btnRow.parentNode.insertBefore(row, btnRow.nextSibling);
    var apply = row.querySelector('#tdCustomApply');
    if (apply) apply.addEventListener('click', applyCustomRange);
  }
}

export function applyCustomRange() {
  var f = document.getElementById('tdCustomFrom');
  var t = document.getElementById('tdCustomTo');
  if (f && f.value) customFrom = f.value;
  if (t && t.value) customTo = t.value;
  if (!customFrom || !customTo) return;
  period = 'custom';
  var label = document.getElementById('tdPeriodLabel');
  if (label) label.textContent = periodLabel('custom');
  load();
}

// Resolve area_id → friendly name via the cached DB.areas.list().
async function ensureAreaNames() {
  if (areaNameById) return;
  areaNameById = {};
  try {
    if (typeof DB !== 'undefined' && DB.areas && DB.areas.list) {
      var areas = await DB.areas.list();
      (areas || []).forEach(function(a){ if (a && a.id != null) areaNameById[String(a.id)] = a.name; });
    }
  } catch (e) { /* leave empty → falls back to 'Area N' labels */ }
}
function areaLabel(aId) {
  var nm = areaNameById && areaNameById[String(aId)];
  return nm || ('Area ' + aId);
}

export function setChart(c) {
  chart = c;
  // v86.4 — class-toggle replaces inline style mutation per button.
  document.querySelectorAll('[data-chart]').forEach(function(btn){
    var isActive = btn.getAttribute('data-chart') === c;
    if (isActive) {
      btn.classList.add('is-active');
    } else {
      btn.classList.remove('is-active');
    }
  });
  renderChart();
}

function populateStores() {
  availableStores = permittedStores();
  // v84.2 — primary store is now the FIRST SELECTED chip, not a fixed
  // "home" store. The v84.1 code set primaryStore once at init from
  // currentUser.store and never updated it. For area managers
  // (currentUser.store is null) this fell back to availableStores[0]
  // and meant Matt couldn't switch the "You" highlight in Ranking or
  // the Top 50 overlay's target store, even when ticking a different
  // chip. Now whichever store is checked first becomes primary — and
  // changes when the user picks a different one.
  //
  // Initial selection: prefer currentUser.store when known (store
  // managers), otherwise the rota screen's currentStore (area mgrs
  // who've already chosen a store via the global header), else the
  // first available.
  if (selectedStores.length === 0) {
    var initial =
      (currentUser && currentUser.store && availableStores.indexOf(currentUser.store) !== -1)
        ? currentUser.store
        : (typeof currentStore === 'string' && availableStores.indexOf(currentStore) !== -1)
          ? currentStore
          : (availableStores[0] || null);
    selectedStores = initial ? [initial] : [];
  } else {
    // Trim any selections that no longer exist (e.g. permission change).
    selectedStores = selectedStores.filter(function(s){ return availableStores.indexOf(s) !== -1; });
  }
  primaryStore = selectedStores[0] || null;

  var list = document.getElementById('tdStoreList');
  if (!list) return;

  if (availableStores.length === 0) {
    list.innerHTML = '<div style="font-size:12px;color:var(--sub)">No stores available.</div>';
    return;
  }
  // v84.1 — escapeAttr() does not exist in index.html scope (only in
  // admin.html). We escape the data-store value manually for the attribute
  // context: replace " with &quot; and & with &amp;. Store names don't
  // contain any other dangerous characters, but this is the safe pattern.
  function escAttrLocal(s){
    return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/"/g, '&quot;');
  }
  list.innerHTML = availableStores.map(function(name){
    var checked  = selectedStores.indexOf(name) !== -1;
    var isPrimary = name === primaryStore;
    var bg = checked ? 'var(--ok-bg)' : 'var(--card)';
    var fc = checked ? 'var(--ok-deep)' : 'var(--text)';
    var bc = checked ? 'var(--ok-border)' : 'var(--border)';
    return '<label style="display:inline-flex;align-items:center;gap:5px;padding:4px 8px;border:1px solid '+bc+';border-radius:6px;font-size:11px;background:'+bg+';color:'+fc+';cursor:pointer">'
      + '<input type="checkbox" data-store="'+escAttrLocal(name)+'"'+(checked?' checked':'')+' onchange="TradeDrives.onStoreToggle(this)" style="cursor:pointer">'
      + escapeHtml(name) + (isPrimary ? ' ⭐' : '')
      + '</label>';
  }).join('');

  var primaryLabel = document.getElementById('tdPrimaryStoreLabel');
  if (primaryLabel) primaryLabel.textContent = primaryStore || 'your store';
}

export function onStoreToggle(input) {
  var name = input.getAttribute('data-store');
  if (input.checked) {
    if (selectedStores.indexOf(name) === -1) selectedStores.push(name);
  } else {
    selectedStores = selectedStores.filter(function(s){ return s !== name; });
  }
  // v84.2 — primary = first selected. Recompute on every toggle so the
  // ⭐ marker, the "You" badge in Ranking, and the Top 50 overlay's
  // target store all move when the user picks a different store.
  primaryStore = selectedStores[0] || null;
  var primaryLabel = document.getElementById('tdPrimaryStoreLabel');
  if (primaryLabel) primaryLabel.textContent = primaryStore || 'your store';
  populateStores();   // re-render chip styling (primary star moves)
  load();
}

export function selectAllStores(all) {
  if (all) {
    // Keep the existing primary at the head of the list when bulk-selecting,
    // so the "You" highlight doesn't jump to the alphabetic first store.
    var keepFirst = primaryStore && availableStores.indexOf(primaryStore) !== -1 ? primaryStore : null;
    selectedStores = keepFirst
      ? [keepFirst].concat(availableStores.filter(function(s){ return s !== keepFirst; }))
      : availableStores.slice();
  } else {
    selectedStores = primaryStore ? [primaryStore] : [];
  }
  primaryStore = selectedStores[0] || null;
  populateStores();
  load();
}

// ── Data fetch + render ─────────────────────────────────────
// v84.1 — fetch flow extended:
//   - /api/kpi_comparison: per-store aggregate (sales/target/LY) — feeds
//     Bars and Heatmap. Single call, fast.
//   - /api/kpi (one per selected store, in parallel): per-day breakdowns
//     — feeds Trend. Cached in lastDaily keyed by store name.
//   - /api/top50: latest report — feeds Trade Driver opportunities.
// v84.0 attempted to read .daily off the comparison response but that
// endpoint doesn't return per-day arrays. Now we explicitly fetch the
// daily series only when needed.
async function load() {
  var area = document.getElementById('tdChartArea');
  if (!area) return;
  if (selectedStores.length === 0) {
    area.innerHTML = '<div style="font-size:13px;color:var(--sub);text-align:center;padding:40px 14px">Pick at least one store.</div>';
    return;
  }
  area.innerHTML = '<div style="font-size:13px;color:var(--sub);text-align:center;padding:40px 14px">Loading…</div>';

  var range = rangeFor(period);
  try {
    // Three parallel branches:
    //   1. aggregate comparison (cheap)
    //   2. per-store daily breakdowns for Trend (one call per selected store)
    //   3. Top 50 latest report
    var [cmp, dailyByStore, top50week] = await Promise.all([
      DB.kpi.comparison({ from: range.from, to: range.to, store: primaryStore }),
      loadDailyForSelected(range),
      loadLatestTop50ForRange(range),
    ]);
    lastData  = cmp;
    lastDaily = dailyByStore || {};
    lastTop50 = top50week;
  } catch (e) {
    console.warn('[trade-drives] load failed:', e && e.message);
    area.innerHTML = '<div style="font-size:13px;color:var(--danger);text-align:center;padding:40px 14px">Couldn\'t load: ' + escapeHtml(e && e.message || 'unknown') + '</div>';
    return;
  }
  await ensureAreaNames();
  renderChart();
  renderTradeDrives();
}

// For each selected store, hit /api/kpi for the year and clip to range.
// /api/kpi returns daily rows shaped { date, store, sales, target, last_year }.
// Parallel fetch; failures degrade gracefully (store simply gets no line).
async function loadDailyForSelected(range) {
  var byStore = {};
  var stores = selectedStores.slice();   // snapshot in case selection changes mid-flight
  if (stores.length === 0) return byStore;
  await Promise.all(stores.map(function(name){
    return DB.kpi.list({ store: name, from: range.from, to: range.to })
      .then(function(rows){
        // Filter to range + non-null sales. Sort by date for the polyline.
        var clipped = (rows || [])
          .filter(function(r){ return r.date >= range.from && r.date <= range.to; })
          .map(function(r){ return { date: r.date, sales: Number(r.sales) || 0 }; })
          .sort(function(a,b){ return a.date.localeCompare(b.date); });
        byStore[name] = clipped;
      })
      .catch(function(e){
        console.warn('[trade-drives] daily load failed for', name, e && e.message);
        byStore[name] = [];
      });
  }));
  return byStore;
}

// Pull the latest Top 50 report whose week overlaps the selected range,
// and return its volume entries. If no overlap, fall back to the most
// recent report we can find (the "what we should push" insight remains
// relevant even if dates don't line up perfectly).
async function loadLatestTop50ForRange(range) {
  try {
    var rRes = await fetch(DB.workerUrl() + '/api/top50/reports', { cache: 'no-store' });
    if (!rRes.ok) return null;
    var reports = await rRes.json();
    if (!Array.isArray(reports) || reports.length === 0) return null;
    // Sort newest first (defensive — API already does this but no harm).
    reports.sort(function(a,b){ return (b.week_start || '').localeCompare(a.week_start || ''); });
    // Try overlap with range first.
    var picked = reports.find(function(r){
      return r.week_start <= range.to && r.week_end >= range.from;
    }) || reports[0];
    var eRes = await fetch(DB.workerUrl() + '/api/top50/entries?report_id=' + picked.id + '&metric=volume', { cache: 'no-store' });
    if (!eRes.ok) return null;
    var entries = await eRes.json();
    return { report: picked, entries: Array.isArray(entries) ? entries : [] };
  } catch (e) {
    console.warn('[trade-drives] top50 load failed:', e && e.message);
    return null;
  }
}

// ── Chart rendering ─────────────────────────────────────────
// Build a per-store summary from the comparison data. Shape:
//   [{ store, sales, target, ly, achievement_pct, growth_pct, daily: [...] }]
// v84.1 — daily now comes from lastDaily (populated per-store by
// /api/kpi calls), not from the comparison response which doesn't
// include per-day breakdowns.
function buildSummary() {
  if (!lastData || !Array.isArray(lastData.rows)) return [];
  var byStore = {};
  lastData.rows.forEach(function(r){ byStore[r.store] = r; });
  return selectedStores.map(function(name){
    var r = byStore[name] || {};
    var sales  = Number(r.sales)  || 0;
    var target = Number(r.target) || 0;
    var ly     = Number(r.last_year_sales || r.ly) || 0;
    return {
      store: name,
      sales: sales,
      target: target,
      ly: ly,
      achievement_pct: target > 0 ? (sales / target * 100) : 0,
      growth_pct: ly > 0 ? ((sales - ly) / ly * 100) : 0,
      daily: lastDaily[name] || [],
    };
  });
}

// v84.1 — Ranking view should show EVERY permitted store (not just the
// selected ones) so the user can see where they actually rank. The
// selection only filters Bars and Trend. Builds the same row shape as
// buildSummary plus the addon fields (v84.2) needed by the Addons view.
// The five addon metrics: demo_units, batteries, gift_wrap, reusable_bag,
// gift_cards — each with `actual_X` and `target_X` columns from the
// /api/kpi_comparison response.
function buildRankingSummary() {
  if (!lastData || !Array.isArray(lastData.rows)) return [];
  var byStore = {};
  lastData.rows.forEach(function(r){ byStore[r.store] = r; });
  return availableStores.map(function(name){
    var r = byStore[name] || {};
    var sales  = Number(r.sales)  || 0;
    var target = Number(r.target) || 0;
    var ly     = Number(r.last_year_sales || r.ly) || 0;
    return {
      store: name,
      sales: sales,
      target: target,
      ly: ly,
      achievement_pct: target > 0 ? (sales / target * 100) : 0,
      growth_pct: ly > 0 ? ((sales - ly) / ly * 100) : 0,
      // v84.2 — addon fields preserved for the Addons view.
      actual_demo_units:    Number(r.actual_demo_units)    || 0,
      target_demo_units:    Number(r.target_demo_units)    || 0,
      actual_batteries:     Number(r.actual_batteries)     || 0,
      target_batteries:     Number(r.target_batteries)     || 0,
      actual_gift_wrap:     Number(r.actual_gift_wrap)     || 0,
      target_gift_wrap:     Number(r.target_gift_wrap)     || 0,
      actual_reusable_bag:  Number(r.actual_reusable_bag)  || 0,
      target_reusable_bag:  Number(r.target_reusable_bag)  || 0,
      actual_gift_cards:    Number(r.actual_gift_cards)    || 0,
      target_gift_cards:    Number(r.target_gift_cards)    || 0,
    };
  });
}

// ── Totals strip (v87) — area / country / business roll-ups ──────────────
// HO/admin: business total + per-country + per-area. Area manager: their
// area total. Staff: hidden (single store is covered by the chart).
function ensureTotalsContainer() {
  var chartArea = document.getElementById('tdChartArea');
  if (!chartArea) return;
  if (!document.getElementById('tdTotals')) {
    var box = document.createElement('div');
    box.id = 'tdTotals';
    box.style.cssText = 'display:none;flex-wrap:wrap;gap:8px;margin-bottom:10px';
    chartArea.parentNode.insertBefore(box, chartArea);
  }
}

function buildTotals() {
  var rows = buildRankingSummary(); // every permitted store (full scope)
  var metaByName = {};
  (Array.isArray(ALL_STORES) ? ALL_STORES : []).forEach(function(s){ if (s && s.name) metaByName[s.name] = s; });
  var byArea = {}, byCountry = {}, biz = { sales: 0, target: 0, ly: 0 };
  function add(bucket, r){ bucket.sales += r.sales; bucket.target += r.target; bucket.ly += r.ly; }
  rows.forEach(function(r){
    var m = metaByName[r.store] || {};
    add(biz, r);
    if (m.area_id != null) { byArea[m.area_id] = byArea[m.area_id] || { sales: 0, target: 0, ly: 0 }; add(byArea[m.area_id], r); }
    var c = m.country || 'Unknown';
    byCountry[c] = byCountry[c] || { sales: 0, target: 0, ly: 0 }; add(byCountry[c], r);
  });
  return { byArea: byArea, byCountry: byCountry, biz: biz };
}

function totalCard(label, agg) {
  var pct = agg.target > 0 ? (agg.sales / agg.target * 100) : 0;
  var col = !agg.target ? 'var(--tiny)' : pct >= 100 ? 'var(--stores-rag-green)' : pct >= 80 ? 'var(--stores-rag-lime)' : pct >= 60 ? 'var(--stores-rag-amber)' : 'var(--stores-rag-red)';
  return '<div style="background:var(--card);border:1px solid var(--border);border-radius:10px;padding:8px 12px;min-width:118px">'
    + '<div style="font-size:9px;letter-spacing:1px;font-weight:700;color:var(--stores-ink-3);text-transform:uppercase">' + escapeHtml(label) + '</div>'
    + '<div style="font-size:15px;font-weight:800;color:var(--text);margin-top:1px">' + fmtMoney(agg.sales) + '</div>'
    + (agg.target > 0 ? '<div style="font-size:10px;font-weight:700;color:' + col + ';margin-top:1px">' + pct.toFixed(0) + '% of target</div>' : '')
    + '</div>';
}

function updateTotals() {
  var box = document.getElementById('tdTotals');
  if (!box) return;
  var role = (currentUser && currentUser.role) || '';
  var isHO = (role === 'admin' || role === 'super_admin');
  var isArea = (role === 'area_manager');
  if ((!isHO && !isArea) || !lastData) { box.style.display = 'none'; box.innerHTML = ''; return; }
  var t = buildTotals();
  var cards = [];
  if (isArea) {
    Object.keys(t.byArea).forEach(function(aId){ cards.push(totalCard(areaLabel(aId) + ' — total', t.byArea[aId])); });
    if (!cards.length) cards.push(totalCard('Total', t.biz));
  } else {
    cards.push(totalCard('Business total', t.biz));
    Object.keys(t.byCountry).sort().forEach(function(c){ cards.push(totalCard(c, t.byCountry[c])); });
    Object.keys(t.byArea).sort(function(a, b){ return areaLabel(a).localeCompare(areaLabel(b)); }).forEach(function(aId){ cards.push(totalCard(areaLabel(aId), t.byArea[aId])); });
  }
  box.style.display = 'flex';
  box.innerHTML = cards.join('');
}

function renderChart() {
  var area = document.getElementById('tdChartArea');
  if (!area) return;
  ensureTotalsContainer();
  updateTotals();
  // Bars and Trend use the selection (focused comparison).
  // Ranking uses the FULL permitted scope so the user can see where they
  // rank against everyone they can see (v84.1 fix — selection wasn't
  // a useful filter for "rank against peers").
  // Addons also uses the full permitted scope — same reasoning: you want
  // to see addon performance across your area, not just where you're
  // currently focused. v84.2.
  if (chart === 'heatmap') {
    var rankRows = buildRankingSummary();
    if (rankRows.length === 0) {
      area.innerHTML = '<div style="font-size:13px;color:var(--sub);text-align:center;padding:40px 14px">No data for this period.</div>';
      return;
    }
    area.innerHTML = renderHeatmap(rankRows);
    return;
  }
  if (chart === 'addons') {
    var addonRows = buildRankingSummary();
    if (addonRows.length === 0) {
      area.innerHTML = '<div style="font-size:13px;color:var(--sub);text-align:center;padding:40px 14px">No data for this period.</div>';
      return;
    }
    area.innerHTML = renderAddons(addonRows);
    return;
  }
  var rows = buildSummary();
  if (rows.length === 0) {
    area.innerHTML = '<div style="font-size:13px;color:var(--sub);text-align:center;padding:40px 14px">No data for this period.</div>';
    return;
  }
  // v86.14: single store with Bars mode → bullet-chart summary instead
  // of a sparse comparison chart. The bars layout works when comparing
  // N stores against each other; with N=1 it's a comparison view with
  // nothing to compare to. See renderSingleStoreSummary() for the
  // replacement layout.
  if (chart === 'bars') {
    if (rows.length === 1) area.innerHTML = renderSingleStoreSummary(rows[0]);
    else                   area.innerHTML = renderBars(rows);
  } else {
    area.innerHTML = renderLines(rows);
  }
}

// v86.14 — single-store summary view (rendered by renderChart when
// chart === 'bars' && selected stores count === 1).
//
// Trade Drivers is a comparison tool; bars-side-by-side works when
// there are multiple stores to compare. With only one store, three
// thin bars in a wide canvas read as a sparse chart with lots of
// empty space, and the achievement % (the actual headline) is small
// and tucked above one of the bars.
//
// This replacement uses a KPI-card layout:
//   - Hero achievement % in tier colour (red <60, amber <80, lime
//     <100, green ≥100)
//   - Bullet chart: Sales (gradient fill) with Target as a vertical
//     tick and LY as a marker below
//   - Three context cards: vs Target, vs Last Year, vs Company avg
//
// Data sources are unchanged — same row shape from buildSummary(),
// plus lastData.company_avg.sales_pct for the third card. The Ranking,
// Trend, and Addons views still use the existing renderers — only
// the Bars view branches.
function renderSingleStoreSummary(row) {
  var sales       = row.sales;
  var target      = row.target;
  var ly          = row.ly;
  var achievement = row.achievement_pct;
  var growthPct   = row.growth_pct;
  var hasTarget   = target > 0;
  var hasLy       = ly > 0;

  // Achievement tier → hero colour. Bands match the existing chart
  // language (green for hitting target, lime for close, amber for
  // mid, red for below). No background tint — just coloured text.
  var heroColor = !hasTarget                  ? 'var(--tiny)'
                : achievement >= 100          ? 'var(--stores-rag-green)'
                : achievement >=  80          ? 'var(--stores-rag-lime)'
                : achievement >=  60          ? 'var(--stores-rag-amber)'
                                              : 'var(--stores-rag-red)';

  // Bullet-chart axis: 10% headroom past the largest value so the
  // tallest bar doesn't slam the right edge of the track.
  var bulletMax = Math.max(sales, target, ly) * 1.10;
  if (bulletMax === 0) bulletMax = 1;
  var salesPct  = (sales  / bulletMax) * 100;
  var targetPct = (target / bulletMax) * 100;
  var lyPct     = (ly     / bulletMax) * 100;

  // Period label for the hero subtitle ("OF YTD TARGET" etc).
  var periodLabels = {
    wtd: 'WEEK-TO-DATE',   lastweek:  'LAST WEEK',
    mtd: 'MONTH-TO-DATE',  lastmonth: 'LAST MONTH',
    ytd: 'YEAR-TO-DATE',
  };
  var periodLabel = periodLabels[period] || 'PERIOD';

  // Company benchmark — already in lastData from /api/kpi_comparison.
  var companyAvgPct = (lastData && lastData.company_avg
                       && typeof lastData.company_avg.sales_pct === 'number')
    ? lastData.company_avg.sales_pct : null;
  var hasCompanyAvg = companyAvgPct !== null && hasTarget;

  // Card deltas.
  var salesVsTarget   = hasTarget     ? (sales - target)         : 0;
  var pctVsTarget     = hasTarget     ? (achievement - 100)      : 0;
  var growthAmount    = hasLy         ? (sales - ly)             : 0;
  var pointsVsCompany = hasCompanyAvg ? (achievement - companyAvgPct) : 0;

  // ── formatters local to this view ──
  function fmtAED(n) { return Math.round(n).toLocaleString(); }
  function fmtDelta(n, type) {
    var sign = n > 0 ? '+' : n < 0 ? '−' : '';   // U+2212 minus
    var abs  = Math.abs(n);
    if (type === 'aed') return sign + 'AED ' + fmtAED(abs);
    if (type === 'pct') return sign + abs.toFixed(0) + '%';
    if (type === 'pts') return sign + abs.toFixed(0) + ' pts';
    return sign + String(abs);
  }
  function deltaColor(n) {
    if (n === 0) return 'var(--tiny)';
    return n > 0 ? 'var(--stores-rag-green)' : 'var(--stores-rag-red)';
  }

  // ── HERO: store name, big %, period label ──
  var heroHtml =
      '<div style="text-align:center;padding:22px 14px 8px">'
    +   '<div style="font-size:10px;color:var(--stores-ink-3);letter-spacing:1.4px;font-weight:700;margin-bottom:4px">STORE</div>'
    +   '<div style="font-size:14px;color:var(--text);font-weight:700">' + escapeHtml(row.store) + '</div>'
    +   '<div style="font-size:60px;line-height:1.05;color:' + heroColor + ';font-weight:800;margin-top:18px;letter-spacing:-1px;font-family:inherit">'
    +     (hasTarget ? Math.round(achievement) + '%' : '—')
    +   '</div>'
    +   '<div style="font-size:10px;color:var(--stores-ink-3);letter-spacing:1.4px;font-weight:700;margin-top:2px">OF ' + periodLabel + ' TARGET</div>'
    + '</div>';

  // ── BULLET CHART: Sales fill, Target tick, LY marker ──
  var targetTick = hasTarget
    ? '<div style="position:absolute;left:' + targetPct.toFixed(2) + '%;top:-4px;width:3px;height:32px;background:var(--text);transform:translateX(-1.5px);border-radius:1px" title="Target: AED ' + fmtAED(target) + '"></div>'
    : '';
  var lyMarker = hasLy
    ? '<div style="position:absolute;left:' + lyPct.toFixed(2) + '%;top:28px;font-size:10px;color:var(--tiny);transform:translateX(-50%);white-space:nowrap;font-weight:700">▲ LY: AED ' + fmtAED(ly) + '</div>'
    : '';
  var bulletBottomPad = hasLy ? '38px' : '22px';
  var bulletHtml =
      '<div style="margin:18px 22px ' + bulletBottomPad + '">'
    +   '<div style="display:flex;justify-content:space-between;font-size:11px;font-weight:700;margin-bottom:8px">'
    +     '<span style="color:var(--text)">Sales: AED ' + fmtAED(sales) + '</span>'
    +     (hasTarget ? '<span style="color:var(--stores-ink-3)">Target: AED ' + fmtAED(target) + '</span>' : '')
    +   '</div>'
    +   '<div style="position:relative;height:24px;background:var(--stores-track);border-radius:6px;overflow:visible">'
    +     '<div style="position:absolute;left:0;top:0;height:100%;width:' + Math.min(100, salesPct).toFixed(2) + '%;background:linear-gradient(to right,#C0DF16,#009E3D);border-radius:6px"></div>'
    +     targetTick
    +     lyMarker
    +   '</div>'
    + '</div>';

  // ── KPI CARDS: vs Target, vs LY, vs Company avg ──
  function buildCard(label, value, sub, color) {
    return '<div style="border:1px solid var(--border);border-radius:8px;padding:12px 8px;text-align:center;background:var(--card);display:flex;flex-direction:column;justify-content:center;min-height:80px">'
      +   '<div style="font-size:10px;color:var(--stores-ink-3);letter-spacing:1.2px;font-weight:700;margin-bottom:6px">' + label + '</div>'
      +   '<div style="font-size:22px;font-weight:800;color:' + color + ';line-height:1">' + value + '</div>'
      +   '<div style="font-size:10px;color:var(--stores-ink-3);font-weight:600;margin-top:4px">' + sub + '</div>'
      + '</div>';
  }
  var card1 = hasTarget
    ? buildCard('VS TARGET', fmtDelta(pctVsTarget, 'pct'), fmtDelta(salesVsTarget, 'aed'), deltaColor(pctVsTarget))
    : buildCard('VS TARGET', '—', 'no target set', 'var(--stores-ink-3)');
  var card2 = hasLy
    ? buildCard('VS LAST YEAR', fmtDelta(growthPct, 'pct'), fmtDelta(growthAmount, 'aed'), deltaColor(growthPct))
    : buildCard('VS LAST YEAR', '—', 'no LY data', 'var(--stores-ink-3)');
  var card3 = hasCompanyAvg
    ? buildCard('VS COMPANY AVG', fmtDelta(pointsVsCompany, 'pts'), 'company at ' + Math.round(companyAvgPct) + '%', deltaColor(pointsVsCompany))
    : buildCard('VS COMPANY AVG', '—', 'no benchmark', 'var(--stores-ink-3)');
  var cardsHtml = '<div style="display:grid;grid-template-columns:repeat(3,1fr);gap:10px;padding:0 14px 18px">'
    + card1 + card2 + card3
    + '</div>';

  return '<div style="font-family:Nunito, ui-rounded, sans-serif">' + heroHtml + bulletHtml + cardsHtml + '</div>';
}

// Bars: three bars per store (Sales, Budget, LY). Best for ≤8 stores.
// v84.2 — handles low store counts gracefully:
//   - Each bar group is capped at 140px wide regardless of canvas size,
//     so a single-store chart doesn't get a 600px-wide bar.
//   - Hide the LY bar entirely (and from the legend) when no store in
//     the selection has LY data — avoids the "+0%" / phantom-bar look
//     when there's no prior-year baseline.
//   - Centre the group(s) horizontally when there are few stores.
function renderBars(rows) {
  // Decide whether the LY series is meaningful — if every row has ly=0,
  // hide it so the chart focuses on sales-vs-budget.
  var showLy = rows.some(function(r){ return (Number(r.ly) || 0) > 0; });
  var seriesCount = showLy ? 3 : 2;

  // Find the max value to set the Y scale.
  var maxV = 0;
  rows.forEach(function(r){
    maxV = Math.max(maxV, r.sales, r.target);
    if (showLy) maxV = Math.max(maxV, r.ly);
  });
  if (maxV === 0) maxV = 1;

  var W = 760, H = 280, padL = 50, padR = 12, padT = 16, padB = 80;
  var innerW = W - padL - padR, innerH = H - padT - padB;
  // Cap group width at 140px so a single store doesn't span the canvas.
  var groupW = Math.min(140, innerW / Math.max(rows.length, 1));
  var totalGroupsW = groupW * rows.length;
  // Centre the cluster horizontally when it doesn't fill the inner area.
  var startX = padL + (innerW - totalGroupsW) / 2;
  var barW   = Math.min(30, (groupW - 16) / seriesCount);
  var gap    = (groupW - barW * seriesCount) / (seriesCount + 1);
  var yScale = function(v){ return innerH - (v / maxV * innerH); };

  var groups = rows.map(function(r, i){
    var x0 = startX + i * groupW + gap;
    var bars = '';
    // v86.2 — Sales: lime→green gradient (ELC primary brand).
    // Budget: solid ELC Lime. LY: neutral brand-ink-4. Rounded corners.
    bars += '<rect x="'+x0+'"             y="'+(padT+yScale(r.sales)) +'" width="'+barW+'" height="'+(innerH - yScale(r.sales))+'"  fill="url(#barSalesV86)" rx="3" ry="3"/>';
    bars += '<rect x="'+(x0+barW+gap)+'" y="'+(padT+yScale(r.target))+'" width="'+barW+'" height="'+(innerH - yScale(r.target))+'" fill="#C0DF16" rx="3" ry="3"/>';
    if (showLy) {
      bars += '<rect x="'+(x0+2*(barW+gap))+'" y="'+(padT+yScale(r.ly))+'" width="'+barW+'" height="'+(innerH - yScale(r.ly))+'"     fill="#B5BEBA" rx="3" ry="3"/>';
    }
    var labelX = startX + i * groupW + groupW / 2;
    var labelY = padT + innerH + 14;
    // Shorten store name for label (e.g. "DUBAI - MARINA MALL" → "Marina Mall")
    var short = (r.store || '').replace(/^[A-Z ]+-\s*/, '');
    bars += '<text x="'+labelX+'" y="'+labelY+'" text-anchor="end" transform="rotate(-40 '+labelX+' '+labelY+')" font-size="9" font-family="Nunito, ui-rounded, sans-serif" font-weight="600" style="fill:var(--tiny)">'+escapeHtml(short)+'</text>';
    bars += '<text x="'+labelX+'" y="'+(padT + yScale(Math.max(r.sales, r.target)) - 4)+'" text-anchor="middle" font-size="10" font-family="Nunito, ui-rounded, sans-serif" font-weight="800" fill="#009E3D">'+Math.round(r.achievement_pct)+'%</text>';
    return bars;
  }).join('');

  // Y-axis ticks
  var ticks = '';
  for (var t = 0; t <= 4; t++) {
    var tv = maxV * t / 4;
    var ty = padT + innerH - (t / 4 * innerH);
    ticks += '<line x1="'+padL+'" y1="'+ty+'" x2="'+(W - padR)+'" y2="'+ty+'" stroke="rgba(26,29,28,0.08)" stroke-width="1"/>';
    ticks += '<text x="'+(padL - 6)+'" y="'+(ty + 3)+'" text-anchor="end" font-size="9" font-family="Nunito, ui-rounded, sans-serif" font-weight="600" fill="#8A9590">'+formatShortNum(tv)+'</text>';
  }

  var legend = '<div style="display:flex;justify-content:center;gap:14px;flex-wrap:wrap;font-size:11px;font-family:var(--brand-font-text);font-weight:600;color:var(--tiny);margin-top:10px">'
    + '<span><span style="display:inline-block;width:10px;height:10px;background:linear-gradient(to top,#009E3D,#C0DF16);border-radius:2px;vertical-align:middle;margin-inline-end:4px"></span>Sales</span>'
    + '<span><span style="display:inline-block;width:10px;height:10px;background:#C0DF16;border-radius:2px;vertical-align:middle;margin-inline-end:4px"></span>Budget</span>'
    + (showLy
        ? '<span><span style="display:inline-block;width:10px;height:10px;background:#B5BEBA;border-radius:2px;vertical-align:middle;margin-inline-end:4px"></span>Last Year</span>'
        : '<span style="color:var(--sub);font-style:italic">No prior-year data for this period</span>')
    + '</div>';

  // SVG gradient def — single source for all Sales bars, referenced via url(#barSalesV86).
  var defs = '<defs>'
    + '<linearGradient id="barSalesV86" x1="0%" y1="0%" x2="0%" y2="100%">'
    +   '<stop offset="0%"   stop-color="#C0DF16"/>'
    +   '<stop offset="100%" stop-color="#009E3D"/>'
    + '</linearGradient>'
    + '</defs>';

  return '<svg viewBox="0 0 '+W+' '+H+'" style="width:100%;height:auto;max-height:340px;font-family:inherit">'
    + defs
    + ticks
    + groups
    + '</svg>' + legend;
}

// Lines: daily sales over period, one polyline per store.
// v84.1 — data now comes from lastDaily (populated by parallel /api/kpi
// calls per selected store). If a store's daily series is empty, that
// store just doesn't get a line — no global fallback needed.
function renderLines(rows) {
  var hasDaily = rows.some(function(r){ return Array.isArray(r.daily) && r.daily.length > 0; });
  if (!hasDaily) {
    return '<div style="font-size:12px;color:var(--sub);text-align:center;padding:40px 14px">No daily data in the selected range for these stores.<br>Bars and Ranking views show period aggregates.</div>';
  }

  // Find x-axis date span and max y
  var allDates = new Set();
  var maxV = 0;
  rows.forEach(function(r){
    r.daily.forEach(function(p){ allDates.add(p.date); maxV = Math.max(maxV, Number(p.sales) || 0); });
  });
  var sortedDates = Array.from(allDates).sort();
  if (sortedDates.length === 0 || maxV === 0) {
    return '<div style="font-size:12px;color:var(--sub);text-align:center;padding:40px 14px">No daily data in range.</div>';
  }

  var W = 760, H = 280, padL = 50, padR = 12, padT = 16, padB = 36;
  var innerW = W - padL - padR, innerH = H - padT - padB;
  var xScale = function(i){ return padL + (sortedDates.length === 1 ? innerW / 2 : i / (sortedDates.length - 1) * innerW); };
  var yScale = function(v){ return padT + innerH - (v / maxV * innerH); };

  // Series palette — bold + high-contrast, ordered so the first stores get the
  // MOST distinct hues (red / blue / purple / orange). HO feedback (2026-06-13):
  // the old green/teal/light-blue lead read the same on a phone chart while on the
  // go. Mobile readability beats brand-green-first here; green moves later in the
  // cycle. Hues + lightness both vary, so adjacent series separate even for most
  // colour-blind viewers and in low-contrast light.
  var COLORS = ['#DC2626','#2563EB','#9333EA','#EA580C','#0D9488','#DB2777','#CA8A04','#16A34A','#4338CA','#0891B2'];
  var paths = '';
  var legendItems = [];
  rows.forEach(function(r, idx){
    var color = COLORS[idx % COLORS.length];
    var byDate = {};
    r.daily.forEach(function(p){ byDate[p.date] = Number(p.sales) || 0; });
    var pts = sortedDates.map(function(d, i){
      var v = byDate[d] || 0;
      return xScale(i) + ',' + yScale(v);
    }).join(' ');
    // Primary store line is thicker so the user's own performance reads first.
    var isPrimary = r.store === primaryStore;
    var sw = isPrimary ? 3 : 2;
    paths += '<polyline points="'+pts+'" fill="none" stroke="'+color+'" stroke-width="'+sw+'" stroke-linejoin="round" stroke-linecap="round"/>';
    var short = (r.store || '').replace(/^[A-Z ]+-\s*/, '');
    legendItems.push('<span style="'+(isPrimary?'font-weight:800;color:var(--text)':'')+'"><span style="display:inline-block;width:12px;height:3px;background:'+color+';vertical-align:middle;margin-inline-end:4px;border-radius:2px"></span>'+escapeHtml(short)+(isPrimary?' · you':'')+'</span>');
  });

  // Y ticks
  var ticks = '';
  for (var t = 0; t <= 4; t++) {
    var ty = padT + innerH - (t / 4 * innerH);
    ticks += '<line x1="'+padL+'" y1="'+ty+'" x2="'+(W - padR)+'" y2="'+ty+'" stroke="rgba(26,29,28,0.08)"/>';
    ticks += '<text x="'+(padL - 6)+'" y="'+(ty + 3)+'" text-anchor="end" font-size="9" font-family="Nunito, ui-rounded, sans-serif" font-weight="600" fill="#8A9590">'+formatShortNum(maxV * t / 4)+'</text>';
  }
  // X-axis: label granularity follows the range span — weekday names for a
  // week-scope, day-of-month through a month, month names across a year.
  function xtick(i, txt) {
    return '<text x="'+xScale(i)+'" y="'+(padT + innerH + 14)+'" text-anchor="middle" font-size="9" font-family="Nunito, ui-rounded, sans-serif" font-weight="600" fill="#8A9590">'+escapeHtml(txt)+'</text>';
  }
  function dParts(d) { return new Date(d + 'T00:00:00Z'); }
  function wkday(d)  { return dParts(d).toLocaleDateString('en-GB', { timeZone: 'UTC', weekday: 'short' }); }
  function dayMon(d) { return dParts(d).toLocaleDateString('en-GB', { timeZone: 'UTC', day: 'numeric', month: 'short' }); }
  function monLbl(d) { return dParts(d).toLocaleDateString('en-GB', { timeZone: 'UTC', month: 'short' }); }
  var spanDays = Math.round((dParts(sortedDates[sortedDates.length - 1]) - dParts(sortedDates[0])) / 86400000) + 1;
  var xLabels = '';
  if (spanDays <= 9) {
    sortedDates.forEach(function(d, i){ xLabels += xtick(i, wkday(d)); });
  } else if (spanDays <= 62) {
    var step = Math.max(1, Math.ceil(sortedDates.length / 6));
    sortedDates.forEach(function(d, i){ if (i % step === 0 || i === sortedDates.length - 1) xLabels += xtick(i, dayMon(d)); });
  } else {
    var seenMonth = {};
    sortedDates.forEach(function(d, i){ var m = d.slice(0, 7); if (!seenMonth[m]) { seenMonth[m] = 1; xLabels += xtick(i, monLbl(d)); } });
  }

  var legend = '<div style="display:flex;justify-content:center;gap:14px;flex-wrap:wrap;font-size:11px;font-family:var(--brand-font-text);font-weight:600;color:var(--tiny);margin-top:10px">' + legendItems.join('') + '</div>';
  return '<svg viewBox="0 0 '+W+' '+H+'" style="width:100%;height:auto;max-height:340px;font-family:inherit">'
    + ticks + xLabels + paths + '</svg>' + legend;
}

// Heatmap: table of stores × {sales, achievement %, growth %} with cell shading.
// v84.1 — rows include every permitted store (not just selection). The
// primary store row gets a green left border + bold to show "this is you".
function renderHeatmap(rows) {
  // Sort by achievement % desc
  var sorted = rows.slice().sort(function(a,b){ return b.achievement_pct - a.achievement_pct; });

  // v86.1 — Hero achievement ring for the user's primary store.
  // Rendered as a pure-SVG component (no JS deps, no chart library) so
  // it streams with the HTML and animates via CSS dash properties.
  // Lime→Green gradient mirrors the v3 mockup; clamped at 150% display.
  var ringHtml = '';
  var myRow = sorted.filter(function(r){ return r.store === primaryStore; })[0];
  if (myRow && Number.isFinite(myRow.achievement_pct)) {
    var myAch  = myRow.achievement_pct;
    var myRank = sorted.indexOf(myRow) + 1;
    var total  = sorted.length;
    // Ring circumference at r=42 is ~264. For pct <= 100 fill proportionally;
    // for pct > 100, draw full ring and use negative dashOffset as overshoot.
    var circ = 264;
    var fillF = Math.min(myAch, 100) / 100;
    var offset = circ * (1 - fillF);
    if (myAch > 100) offset = -Math.min(((myAch - 100) / 100) * circ, circ * 0.5);
    var msg = myAch >= 100
      ? 'Hitting target — well done.'
      : (myAch >= 80
          ? 'Close to target — push the Trade Drivers list.'
          : 'Below target — focus on the items below.');
    ringHtml =
      '<div style="background:var(--card);border:1px solid var(--brand-hairline);border-radius:var(--brand-r-card);padding:16px 18px;margin-bottom:14px;display:grid;grid-template-columns:auto 1fr;gap:16px;align-items:center;box-shadow:0 1px 3px rgba(0,0,0,0.03),0 8px 24px -16px rgba(0,158,61,0.10);overflow:hidden;position:relative">'
      +   '<div style="position:absolute;top:-40px;right:-40px;width:140px;height:140px;background:radial-gradient(circle,#C0DF16 0%,transparent 65%);opacity:0.16;pointer-events:none"></div>'
      +   '<svg viewBox="0 0 100 100" width="92" height="92" style="flex-shrink:0;position:relative">'
      +     '<defs>'
      +       '<linearGradient id="ringG_v86" x1="0%" y1="0%" x2="100%" y2="100%">'
      +         '<stop offset="0%" stop-color="#C0DF16"/>'
      +         '<stop offset="100%" stop-color="#009E3D"/>'
      +       '</linearGradient>'
      +     '</defs>'
      +     '<circle cx="50" cy="50" r="42" fill="none" style="stroke:var(--elc-green-soft)" stroke-width="9"/>'
      +     '<circle cx="50" cy="50" r="42" fill="none" stroke="url(#ringG_v86)" stroke-width="9" stroke-linecap="round" '
      +       'stroke-dasharray="'+circ+'" stroke-dashoffset="'+offset.toFixed(1)+'" '
      +       'transform="rotate(-90 50 50)"/>'
      +     '<text x="50" y="49" text-anchor="middle" font-family="Nunito, ui-rounded, sans-serif" font-size="20" font-weight="800" style="fill:var(--text)" letter-spacing="-0.02em">'+Math.round(myAch)+'%</text>'
      +     '<text x="50" y="63" text-anchor="middle" font-family="Nunito, ui-rounded, sans-serif" font-size="7" font-weight="700" style="fill:var(--tiny)" letter-spacing="0.08em">OF TARGET</text>'
      +   '</svg>'
      +   '<div style="position:relative">'
      +     '<div style="font-family:var(--brand-font);font-size:11px;font-weight:700;color:var(--elc-green);text-transform:uppercase;letter-spacing:0.08em;margin-bottom:3px">'+escapeHtml(primaryStore)+'</div>'
      +     '<div style="font-family:var(--brand-font);font-size:18px;font-weight:800;color:var(--text);letter-spacing:-0.015em;line-height:1.15;margin-bottom:3px">Rank '+myRank+' of '+total+'</div>'
      +     '<div style="font-family:var(--brand-font-text);font-size:12px;color:var(--sub);line-height:1.4">'+msg+'</div>'
      +   '</div>'
      + '</div>';
  }

  var html = ringHtml + '<div style="overflow-x:auto"><table style="width:100%;border-collapse:collapse;font-size:12px">'
    + '<thead><tr style="background:var(--surface-2);text-align:start">'
    + '<th style="padding:8px;border-bottom:1px solid var(--border)">Rank</th>'
    + '<th style="padding:8px;border-bottom:1px solid var(--border)">Store</th>'
    + '<th style="padding:8px;border-bottom:1px solid var(--border);text-align:end">Sales</th>'
    + '<th style="padding:8px;border-bottom:1px solid var(--border);text-align:end">Budget</th>'
    + '<th style="padding:8px;border-bottom:1px solid var(--border);text-align:end">% Achieved</th>'
    + '<th style="padding:8px;border-bottom:1px solid var(--border);text-align:end">vs LY</th>'
    + '</tr></thead><tbody>';

  sorted.forEach(function(r, i){
    var ach = r.achievement_pct;
    var growth = r.growth_pct;
    // v86.1 — ELC brand palette replaces the traffic-light scheme.
    // Achievement: green-soft/green for hit; warm-yellow/preorder for near;
    // soft-coral/coming-soon for miss. Growth: green for up, coral for down.
    var achBg = ach >= 100 ? 'var(--elc-green-soft)' : (ach >= 80 ? 'var(--stores-amber-soft)' : 'var(--danger-bg)');
    var achFg = ach >= 100 ? 'var(--elc-green)'      : (ach >= 80 ? 'var(--elc-pos-preorder)' : 'var(--elc-pos-coming)');
    var grBg  = growth >= 0 ? 'var(--elc-green-soft)' : 'var(--danger-bg)';
    var grFg  = growth >= 0 ? 'var(--elc-green)'      : 'var(--elc-pos-coming)';
    var isPrimary = r.store === primaryStore;
    // v86.1 — primary-row treatment now uses ELC brand green (#009E3D)
    // not the legacy WhatsApp --green (var(--brand,#009E3D)).
    var rowStyle = isPrimary
      ? 'background:var(--elc-green-soft);border-inline-start:3px solid var(--elc-green)'
      : '';
    var nameWeight = isPrimary ? '800' : '600';
    var youBadge   = isPrimary ? ' <span style="font-size:10px;color:#fff;font-weight:800;text-transform:uppercase;letter-spacing:0.6px;background:var(--elc-green);padding:2px 7px;border-radius:6px;margin-inline-start:6px;font-family:var(--brand-font)">You</span>' : '';
    html += '<tr style="'+rowStyle+'">'
      + '<td style="padding:8px;border-bottom:1px solid var(--border);color:var(--sub);font-weight:700;font-family:var(--brand-font);font-variant-numeric:tabular-nums">'+(i+1)+'</td>'
      + '<td style="padding:8px;border-bottom:1px solid var(--border)"><span style="font-weight:'+nameWeight+'">'+escapeHtml(r.store)+'</span>'+youBadge+'</td>'
      + '<td style="padding:8px;border-bottom:1px solid var(--border);text-align:end;font-variant-numeric:tabular-nums">'+formatShortNum(r.sales)+'</td>'
      + '<td style="padding:8px;border-bottom:1px solid var(--border);text-align:end;font-variant-numeric:tabular-nums">'+formatShortNum(r.target)+'</td>'
      + '<td style="padding:8px;border-bottom:1px solid var(--border);text-align:end"><span style="background:'+achBg+';color:'+achFg+';padding:2px 8px;border-radius:10px;font-weight:700;font-variant-numeric:tabular-nums;font-family:var(--brand-font)">'+Math.round(ach)+'%</span></td>'
      + '<td style="padding:8px;border-bottom:1px solid var(--border);text-align:end"><span style="background:'+grBg+';color:'+grFg+';padding:2px 8px;border-radius:10px;font-weight:700;font-variant-numeric:tabular-nums;font-family:var(--brand-font)">'+(growth >= 0 ? '+' : '')+Math.round(growth)+'%</span></td>'
      + '</tr>';
  });
  html += '</tbody></table></div>';
  return html;
}

// Addons view (v84.2). Per-store table with:
//   - One column per addon (Demo / Bat / Wrap / Bag / Cards): achievement %
//     coloured red/amber/green like the main Ranking.
//   - Final column: addons as % of total sales (revenue contribution).
//     Computed as (Σ actual_X × unit_price_X) / sales × 100. Demo is
//     skipped from the revenue sum because its price varies per
//     country/week and isn't on the addon_rates row.
// User can sort the table by clicking a metric header — defaults to
// ranking by overall sales achievement so the layout matches the
// Ranking view's order.
function renderAddons(rows) {
  var rates = lastAddonRates || {};
  var batPrice  = Number(rates.batteries_price)    || 0;
  var wrapPrice = Number(rates.gift_wrap_price)    || 0;
  var bagPrice  = Number(rates.reusable_bag_price) || 0;
  var cardPrice = Number(rates.gift_cards_price)   || 0;

  // Decorate each row with the four addon-% achievements + the overall
  // addon-revenue ratio.
  var decorated = rows.map(function(r){
    function pct(actual, target) { return target > 0 ? (actual / target * 100) : null; }
    var demoP  = pct(r.actual_demo_units,    r.target_demo_units);
    var batP   = pct(r.actual_batteries,     r.target_batteries);
    var wrapP  = pct(r.actual_gift_wrap,     r.target_gift_wrap);
    var bagP   = pct(r.actual_reusable_bag,  r.target_reusable_bag);
    var cardP  = pct(r.actual_gift_cards,    r.target_gift_cards);
    // Addon revenue contribution (skip demo — variable pricing).
    var addonRevenue =
      (r.actual_batteries    * batPrice)  +
      (r.actual_gift_wrap    * wrapPrice) +
      (r.actual_reusable_bag * bagPrice)  +
      (r.actual_gift_cards   * cardPrice);
    var addonPctOfSales = r.sales > 0 ? (addonRevenue / r.sales * 100) : null;
    return {
      store: r.store,
      sales: r.sales,
      achievement_pct: r.achievement_pct,
      demo_pct: demoP,
      bat_pct:  batP,
      wrap_pct: wrapP,
      bag_pct:  bagP,
      card_pct: cardP,
      addon_pct_of_sales: addonPctOfSales,
    };
  });

  // Sort by sales achievement desc so the layout mirrors the Ranking tab.
  decorated.sort(function(a,b){ return b.achievement_pct - a.achievement_pct; });

  function cellPct(p) {
    if (p == null) return '<td style="padding:6px 8px;border-bottom:1px solid var(--border);text-align:end;color:var(--sub)">—</td>';
    // v86.1 — ELC brand palette replaces the traffic-light bg/fg.
    // ≥100% → green-soft + ELC green
    //  80%+ → soft warm yellow + Preorder orange
    //  <80% → soft warm coral + Coming-soon red
    var bg = p >= 100 ? 'var(--elc-green-soft)' : (p >= 80 ? 'var(--stores-amber-soft)' : 'var(--danger-bg)');
    var fg = p >= 100 ? 'var(--elc-green)'      : (p >= 80 ? 'var(--elc-pos-preorder)' : 'var(--elc-pos-coming)');
    return '<td style="padding:6px 8px;border-bottom:1px solid var(--border);text-align:end"><span style="background:'+bg+';color:'+fg+';padding:2px 7px;border-radius:10px;font-weight:700;font-variant-numeric:tabular-nums;font-family:var(--brand-font)">'+Math.round(p)+'%</span></td>';
  }

  function cellPctSales(p) {
    // For "% of sales" the bands are different — this is attachment rate,
    // not achievement. Use neutral styling unless rates are missing.
    if (p == null) return '<td style="padding:6px 8px;border-bottom:1px solid var(--border);text-align:end;color:var(--sub)">—</td>';
    return '<td style="padding:6px 8px;border-bottom:1px solid var(--border);text-align:end"><span style="background:var(--info-bg);color:var(--info-deep);padding:2px 7px;border-radius:10px;font-weight:600;font-variant-numeric:tabular-nums">'+p.toFixed(1)+'%</span></td>';
  }

  var html = '<div style="overflow-x:auto"><table style="width:100%;border-collapse:collapse;font-size:12px">'
    + '<thead><tr style="background:var(--surface-2);text-align:start">'
    +   '<th style="padding:8px;border-bottom:1px solid var(--border)">Rank</th>'
    +   '<th style="padding:8px;border-bottom:1px solid var(--border)">Store</th>'
    +   '<th style="padding:8px;border-bottom:1px solid var(--border);text-align:end" title="Sales achievement %">Sales %</th>'
    +   '<th style="padding:8px;border-bottom:1px solid var(--border);text-align:end" title="Demo achievement %">Demo %</th>'
    +   '<th style="padding:8px;border-bottom:1px solid var(--border);text-align:end" title="Batteries achievement %">Bat %</th>'
    +   '<th style="padding:8px;border-bottom:1px solid var(--border);text-align:end" title="Gift wrap achievement %">Wrap %</th>'
    +   '<th style="padding:8px;border-bottom:1px solid var(--border);text-align:end" title="Reusable bag achievement %">Bag %</th>'
    +   '<th style="padding:8px;border-bottom:1px solid var(--border);text-align:end" title="Gift cards achievement %">Cards %</th>'
    +   '<th style="padding:8px;border-bottom:1px solid var(--border);text-align:end" title="Total addon revenue as % of total sales (excludes demo — variable pricing)">Addons % of Sales</th>'
    + '</tr></thead><tbody>';

  decorated.forEach(function(r, i){
    var isPrimary = r.store === primaryStore;
    // v86.1 — match renderHeatmap brand treatment.
    var rowStyle = isPrimary ? 'background:var(--elc-green-soft);border-inline-start:3px solid var(--elc-green)' : '';
    var nameWeight = isPrimary ? '800' : '600';
    var youBadge   = isPrimary ? ' <span style="font-size:10px;color:#fff;font-weight:800;text-transform:uppercase;letter-spacing:0.6px;background:var(--elc-green);padding:2px 7px;border-radius:6px;margin-inline-start:6px;font-family:var(--brand-font)">You</span>' : '';
    html += '<tr style="'+rowStyle+'">'
      + '<td style="padding:6px 8px;border-bottom:1px solid var(--border);color:var(--sub);font-weight:700;font-family:var(--brand-font);font-variant-numeric:tabular-nums">'+(i+1)+'</td>'
      + '<td style="padding:6px 8px;border-bottom:1px solid var(--border)"><span style="font-weight:'+nameWeight+'">'+escapeHtml(r.store)+'</span>'+youBadge+'</td>'
      + cellPct(r.achievement_pct)
      + cellPct(r.demo_pct)
      + cellPct(r.bat_pct)
      + cellPct(r.wrap_pct)
      + cellPct(r.bag_pct)
      + cellPct(r.card_pct)
      + cellPctSales(r.addon_pct_of_sales)
      + '</tr>';
  });
  html += '</tbody></table></div>';

  // Note explaining the demo exclusion + missing rates.
  if (!lastAddonRates) {
    html += '<div style="font-size:11px;color:var(--warn-deep);background:var(--warn-bg);border-radius:6px;padding:6px 10px;margin-top:10px">⚠ Addon prices not loaded yet — "Addons % of Sales" will show once <code>/api/addon_rates</code> responds.</div>';
  } else {
    html += '<div style="font-size:11px;color:var(--sub);margin-top:10px;line-height:1.5">"Addons % of Sales" = battery + wrap + bag + cards revenue as a share of total sales. Demo is excluded because its price varies per store/country and isn\'t fixed.</div>';
  }
  return html;
}

function formatShortNum(n) {
  if (!Number.isFinite(n) || n === 0) return '0';
  if (Math.abs(n) >= 1e6) return (n / 1e6).toFixed(2) + 'M';
  if (Math.abs(n) >= 1e3) return (n / 1e3).toFixed(0) + 'K';
  return String(Math.round(n));
}

// ── Trade-driver overlay: SOH > 0 but qty = 0 in primary store ─────
// v85.0 — uses Top50.shared.findTop50StoreKey, which consults
// stores.upload_name (the explicit alias) first and falls back to
// normalised-name matching only when no alias is set.
//
// Why this matters: the Top 50 spreadsheet upload uses store names
// from a different source system than the rota/stores table. Most
// stores match cleanly after lowercase + "ELC -" prefix strip (the
// v80.4 fix). A handful drift further — e.g. "Dubai - Arabian Ranches 2"
// in the stores table is "ELC - DUBAI - ARABIAN RANCHES 2 COMMUNITY
// CENTRE" in the spreadsheet. The upload_name column (set per store
// in Admin → Top 50 → Store Mapping) handles those exact cases.
function renderTradeDrives() {
  var el = document.getElementById('tdTradeDriveList');
  if (!el) return;
  if (!lastTop50 || !primaryStore) {
    el.innerHTML = '<div style="color:var(--sub);text-align:center;padding:20px;font-size:12px">No Top 50 data available.</div>';
    return;
  }
  var entries = lastTop50.entries || [];

  // Build the canonicalName → upload_name map once per render (cheap —
  // 28 stores). Use the shared helper if loaded; fall back to a local
  // implementation otherwise. The fallback exists for the rare case
  // where dist/top50.js failed to load — Trade Drivers shouldn't be
  // a hard dependency on the bundle.
  var aliasMap = (typeof Top50 !== 'undefined' && Top50.shared && Top50.shared.buildTop50AliasMap)
    ? Top50.shared.buildTop50AliasMap(ALL_STORES)
    : (function(){
        var m = {};
        (ALL_STORES || []).forEach(function(s){
          if (s && s.name) m[s.name] = (s.upload_name && String(s.upload_name).trim()) || null;
        });
        return m;
      })();

  // Per-entry key resolver. Tries alias first, normalised name second.
  function findKey(canonical, keys) {
    if (typeof Top50 !== 'undefined' && Top50.shared && Top50.shared.findTop50StoreKey) {
      return Top50.shared.findTop50StoreKey(canonical, aliasMap, keys);
    }
    // Local fallback mirroring shared.js findTop50StoreKey logic.
    if (!canonical || !Array.isArray(keys) || keys.length === 0) return null;
    var alias = aliasMap[canonical];
    if (alias) {
      if (keys.indexOf(alias) !== -1) return alias;
      var aLower = String(alias).toLowerCase();
      for (var i = 0; i < keys.length; i++) {
        if (String(keys[i]).toLowerCase() === aLower) return keys[i];
      }
    }
    var norm = function(s){
      return String(s == null ? '' : s).toLowerCase()
        .replace(/^\s*elc\s*-\s*/i, '').replace(/\s+/g, ' ').trim();
    };
    var canonNorm = norm(canonical);
    for (var j = 0; j < keys.length; j++) {
      if (norm(keys[j]) === canonNorm) return keys[j];
    }
    return null;
  }

  // Filter to items where the primary store has SOH > 0 but no sales.
  var drives = entries.filter(function(e){
    if (!e.store_data) return false;
    var matchKey = findKey(primaryStore, Object.keys(e.store_data));
    if (!matchKey) return false;
    var d = e.store_data[matchKey];
    var soh = Number(d.soh) || 0;
    var qty = Number(d.qty) || 0;
    return soh > 0 && qty === 0;
  });
  drives.sort(function(a,b){ return (a.rank || 999) - (b.rank || 999); });

  if (drives.length === 0) {
    // Two ways to reach this empty state: (a) genuinely no zero-sales
    // items at the store; (b) the store name doesn't resolve to any
    // Top 50 key — either upload_name isn't set, or the spreadsheet
    // genuinely doesn't include this store this week. Surface (b)
    // clearly so admins can fix the alias from the new mapping tab.
    var storeFoundInTop50 = entries.some(function(e){
      if (!e.store_data) return false;
      return findKey(primaryStore, Object.keys(e.store_data)) !== null;
    });
    if (!storeFoundInTop50) {
      // v86.2 — warning uses brand preorder orange instead of generic amber.
      el.innerHTML = '<div style="color:var(--elc-pos-preorder);text-align:center;padding:16px;font-size:12px;background:var(--stores-amber-soft);border-radius:var(--brand-r-inner);font-family:var(--brand-font-text);font-weight:600;line-height:1.4">⚠ Couldn\'t find '+escapeHtml(primaryStore)+' in the Top 50 file. Ask admin to set the store alias in Admin → Top 50 → Store Mapping.</div>';
      return;
    }
    // v86.2 — success uses ELC brand green.
    el.innerHTML = '<div style="color:var(--elc-green);text-align:center;padding:16px;font-size:12px;background:var(--elc-green-soft);border-radius:var(--brand-r-inner);font-family:var(--brand-font-text);font-weight:600;line-height:1.4">✓ No zero-sales-in-stock items at '+escapeHtml(primaryStore)+' this week. You\'re selling everything you have.</div>';
    return;
  }

  // Compact list with rank, item code, description, SOH.
  el.innerHTML = '<div style="font-size:11px;color:var(--sub);margin-bottom:8px;font-family:var(--brand-font-text);font-weight:600">'
    + drives.length + ' item' + (drives.length === 1 ? '' : 's') + ' · week of ' + escapeHtml(lastTop50.report.week_start)
    + '</div>'
    + '<div style="display:grid;grid-template-columns:50px 90px 1fr 60px;gap:6px;font-size:11px;color:var(--sub);font-weight:700;text-transform:uppercase;letter-spacing:0.6px;padding:6px 8px;border-bottom:1px solid var(--brand-hairline);font-family:var(--brand-font)">'
    +   '<div>Rank</div><div>Code</div><div>Description</div><div style="text-align:end">SOH</div>'
    + '</div>'
    + drives.map(function(d){
      var matchKey = findKey(primaryStore, Object.keys(d.store_data || {}));
      var sd = matchKey ? d.store_data[matchKey] : null;
      return '<div style="display:grid;grid-template-columns:50px 90px 1fr 60px;gap:6px;padding:8px;border-bottom:1px solid var(--surface-3);align-items:center">'
        + '<div style="font-weight:800;font-family:var(--brand-font);color:var(--elc-green)">#'+(d.rank || '?')+'</div>'
        + '<div style="font-variant-numeric:tabular-nums;color:var(--sub);font-family:ui-monospace,monospace;font-size:11px">'+escapeHtml(d.item_code || '—')+'</div>'
        + '<div translate="no" style="font-weight:600">'+escapeHtml(d.description || '—')+'</div>'   // v135.1: product text from the file
        + '<div style="text-align:end;font-variant-numeric:tabular-nums;font-weight:800;color:var(--elc-green);font-family:var(--brand-font);font-size:14px">'+((sd && sd.soh) || 0)+'</div>'
        + '</div>';
    }).join('');
}

function checkMobile() {
  var hint = document.getElementById('tdMobileHint');
  if (!hint) return;
  hint.style.display = (window.innerWidth < 700) ? '' : 'none';
}

export function onTabOpen() {
  populateStores();
  checkMobile();
  ensureCustomControls();
  ensureTotalsContainer();
  ensureAreaNames();
  // v84.2 — load addon rates once; they don't change between scans and
  // the Addons view needs them to convert addon counts → revenue → %
  // of total sales.
  if (!lastAddonRates) {
    fetch(DB.workerUrl() + '/api/addon_rates', { cache: 'no-store' })
      .then(function(r){ return r.ok ? r.json() : null; })
      .then(function(rows){
        if (Array.isArray(rows) && rows.length > 0) lastAddonRates = rows[0];
        if (chart === 'addons') renderChart();
      })
      .catch(function(){ /* fall back gracefully — rates section just shows '—' */ });
  }
  setPeriod(period);  // Triggers load() + render
}

// Re-check mobile width when the viewport changes. Previously sat
// in index.html immediately after the IIFE close; moved here in
// v86.11 so the module owns its viewport-watching side effect.
window.addEventListener('resize', function(){
  var hint = document.getElementById('tdMobileHint');
  if (hint) hint.style.display = (window.innerWidth < 700) ? '' : 'none';
});
